import type { SalesDashboardDatasetType } from '@/views/reporting-dashboards/salesDashboardTabs'

const CREATED_EXCLUDED_PATTERN = /cancelled|canceled|voided/i
const INVOICED_DEFAULT_PATTERN = /^exported$/i

export function isExcludedCreatedStatus(value: string): boolean {
  return CREATED_EXCLUDED_PATTERN.test(value.trim())
}

export function defaultCreatedStatuses(
  orderOptions: string[],
  productOptions: string[]
): { order: string[]; product: string[] } {
  return {
    order: orderOptions.filter((s) => !isExcludedCreatedStatus(s)),
    product: productOptions.filter((s) => !isExcludedCreatedStatus(s)),
  }
}

export function defaultInvoicedStatuses(invoiceOptions: string[]): string[] {
  return invoiceOptions.filter((s) => INVOICED_DEFAULT_PATTERN.test(s.trim()))
}

export interface SalesDashboardStatusOptions {
  order: string[]
  product: string[]
  invoice: string[]
}

export interface SalesDashboardAppliedStatus {
  order: string[]
  product: string[]
  invoice: string[]
}

function sortedKey(values: string[]): string {
  return [...values].sort().join('\0')
}

function arraysEqualSorted(a: string[], b: string[]): boolean {
  return sortedKey(a) === sortedKey(b)
}

export function defaultStatusSelectionForDataset(
  dataset: SalesDashboardDatasetType,
  options: SalesDashboardStatusOptions
): { order: string[]; product: string[]; invoice: string[] } {
  if (dataset === 'created') {
    const { order, product } = defaultCreatedStatuses(options.order, options.product)
    return { order, product, invoice: [] }
  }
  return {
    order: [],
    product: [],
    invoice: defaultInvoicedStatuses(options.invoice),
  }
}

/** Keep only status values that exist in the current option lists for the dataset. */
export function pruneStatusSelectionToOptions(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): SalesDashboardAppliedStatus {
  if (dataset === 'created') {
    const orderSet = new Set(options.order)
    const productSet = new Set(options.product)
    return {
      order: applied.order.filter((s) => orderSet.has(s)),
      product: applied.product.filter((s) => productSet.has(s)),
      invoice: [],
    }
  }
  const invoiceSet = new Set(options.invoice)
  return {
    order: [],
    product: [],
    invoice: applied.invoice.filter((s) => invoiceSet.has(s)),
  }
}

export function statusSelectionEqualsDefault(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): boolean {
  const defaults = defaultStatusSelectionForDataset(dataset, options)
  if (dataset === 'created') {
    return (
      arraysEqualSorted(applied.order, defaults.order) &&
      arraysEqualSorted(applied.product, defaults.product)
    )
  }
  return arraysEqualSorted(applied.invoice, defaults.invoice)
}

/** Whether the status filter chip should appear for the current applied selection and options. */
export function shouldShowStatusFilterChip(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): boolean {
  const pruned = pruneStatusSelectionToOptions(dataset, applied, options)
  if (dataset === 'created') {
    if (pruned.order.length === 0 && pruned.product.length === 0) return false
  } else if (pruned.invoice.length === 0) {
    return false
  }

  if (statusSelectionEqualsDefault(dataset, pruned, options)) {
    const defaults = defaultStatusSelectionForDataset(dataset, options)
    if (dataset === 'created') {
      return defaults.order.length > 0 || defaults.product.length > 0
    }
    return defaults.invoice.length > 0
  }

  return true
}

export function formatStatusChipValue(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): string {
  const pruned = pruneStatusSelectionToOptions(dataset, applied, options)
  if (statusSelectionEqualsDefault(dataset, pruned, options)) {
    return dataset === 'created' ? 'All except Cancelled, voided' : 'Exported'
  }
  const count =
    dataset === 'created'
      ? pruned.order.length + pruned.product.length
      : pruned.invoice.length
  return `${count} values`
}

export function appliedStatusValuesForChip(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): string[] {
  const pruned = pruneStatusSelectionToOptions(dataset, applied, options)
  return dataset === 'created'
    ? [...pruned.order, ...pruned.product]
    : [...pruned.invoice]
}

/** Remove GL codes that are not in the current option list. */
export function sanitizeGlCodesForOptions(gl: string[], glOptions: string[]): string[] {
  if (glOptions.length === 0) return []
  const allowed = new Set(glOptions)
  return gl.filter((code) => allowed.has(code))
}

/**
 * Sanitize status arrays for the active dataset using current option lists.
 * Use before Apply and when persisting applied filters after option reloads.
 */
export function sanitizeStatusFiltersForContext(
  dataset: SalesDashboardDatasetType,
  applied: SalesDashboardAppliedStatus,
  options: SalesDashboardStatusOptions
): SalesDashboardAppliedStatus {
  return pruneStatusSelectionToOptions(dataset, applied, options)
}

export type StatusSelectionCacheEntry =
  | {
      kind: 'user'
      status: SalesDashboardAppliedStatus
      gl: string[]
    }
  | { kind: 'cleared' }

/** Default status selection for a dataset given current option lists. */
export function getDefaultStatusesForContext(
  dataset: SalesDashboardDatasetType,
  options: SalesDashboardStatusOptions
): SalesDashboardAppliedStatus {
  return defaultStatusSelectionForDataset(dataset, options)
}

/**
 * Resolve draft/applied status + GL for a filter context.
 * - user: restore pruned prior selection
 * - cleared: empty (no auto-default)
 * - undefined: product defaults (e.g. Exported when available)
 */
export function resolveStatusSelectionForContext(
  dataset: SalesDashboardDatasetType,
  options: SalesDashboardStatusOptions,
  glOptions: string[],
  selectionEntry?: StatusSelectionCacheEntry
): { status: SalesDashboardAppliedStatus; gl: string[] } {
  if (selectionEntry?.kind === 'cleared') {
    return {
      status: { order: [], product: [], invoice: [] },
      gl: [],
    }
  }

  if (selectionEntry?.kind === 'user') {
    return {
      status: pruneStatusSelectionToOptions(dataset, selectionEntry.status, options),
      gl: sanitizeGlCodesForOptions(selectionEntry.gl, glOptions),
    }
  }

  return {
    status: getDefaultStatusesForContext(dataset, options),
    gl: [],
  }
}
